import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeToOrderService } from "../../utils/services/m2oService";
import { asnService } from "../../utils/services/asnService";
import { orderService } from "../../utils/services/orderService";
import { formatDate } from "../../utils/formatDate";
import { warehouseLabel } from "../../utils/warehouseLabel";

// M2O Order Details Modal Component
const M2OOrderDetailsModal = ({ open, onClose, orderData, onEdit }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [asnData, setAsnData] = useState(null);

  useEffect(() => {
    const fetchM2OOrderData = async () => {
      if (!orderData?._id || !open) {
        return;
      }

      try {
        setIsLoading(true);
        setError("");

        // Fetch the M2O order data
        const m2oResponse = await asnService.fetchById(
          orderData.purchase_order,
        );
        setAsnData(m2oResponse);
      } catch (error) {
        console.error("Error fetching M2O order:", error);
        setError(error?.response?.data?.message || "Failed to load order data");
      } finally {
        setIsLoading(false);
      }
    };

    if (open && orderData.purchase_order) {
      fetchM2OOrderData();
    }
  }, [orderData, open]);

  const renderAddress = (address, type) => {
    return (
      <Grid item xs={12} md={6}>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {type}
            </Typography>
            <Typography variant="body2">
              Name: {`${address?.first_name || ""} ${address?.last_name || ""}`}
            </Typography>

            <Typography variant="body2">
              Email: {address?.email || ""}
            </Typography>
            {address?.company && (
              <Typography variant="body2">
                Company: {address.company}
              </Typography>
            )}
            <Typography variant="body2">
              Address:{" "}
              {address?.address1
                ? address?.address1
                : address?.address?.address1 || ""}
            </Typography>
            {address?.address2 && (
              <Typography variant="body2">
                Address 2:{" "}
                {address.address2
                  ? address?.address2
                  : address?.address?.address2 || "-"}
              </Typography>
            )}
            {address?.city || address?.address?.city ? (
              <Typography variant="body2">
                City:{" "}
                {`${
                  address?.city ? address?.city : address?.address?.city || ""
                }`}
              </Typography>
            ) : (
              <></>
            )}
            <Typography variant="body2">
              State:{" "}
              {(() => {
                const province =
                  address?.province || address?.address?.province || "";
                const zip = address?.zip || address?.address?.zip || "";
                if (province && zip) return `${province}, ${zip}`;
                if (province) return province;
                if (zip) return zip;
                return "";
              })()}
            </Typography>

            <Typography variant="body2">
              Country:{" "}
              {address?.country
                ? address?.country
                : address?.address?.country || ""}
            </Typography>
            {address?.phone ? (
              <Typography variant="body2">
                Phone: {address?.phone || ""}
              </Typography>
            ) : (
              <></>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
          borderRadius: 1,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Make-to-Order Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading order details...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !orderData ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No order data found
          </Alert>
        ) : (
          <Paper
            elevation={0}
            sx={{
              maxHeight: "calc(80vh - 120px)",
              overflowY: "auto",
              width: "100%",
            }}
          >
            {/* Order Summary Section */}
            <Card sx={{ mb: 3, backgroundColor: "#f9f9f9" }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Order ID
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderData.order_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Order Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(orderData.order_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={orderData.status}
                      color={
                        ["Fulfilled"].includes(orderData.status.toLowerCase())
                          ? "success"
                          : ["Cancelled", "On Hold"].includes(
                                orderData.status.toLowerCase(),
                              )
                            ? "error"
                            : "warning"
                      }
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Customer and Shipping Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Customer & Shipping Information
              </Typography>
              <Grid container spacing={3}>
                {/* Shipping Address */}
                {renderAddress(orderData.shipping_address, "Shipping Address")}

                {/* Customer Address */}
                {renderAddress(orderData.customer, "Customer Address")}
              </Grid>
            </Box>

            {/* Order Items */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order Items
              </Typography>
              <Paper variant="outlined" sx={{ p: 0 }}>
                <List disablePadding>
                  <ListItem
                    sx={{
                      py: 1,
                      px: 2,
                      bgcolor: "grey.100",
                      display: "flex",
                    }}
                  >
                    <ListItemText
                      primary="Product"
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: 3 }}
                    />
                    <ListItemText
                      primary="SKU"
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: 2 }}
                    />
                    <ListItemText
                      primary="Qty Requested"
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: 1, textAlign: "center" }}
                    />
                    <ListItemText
                      primary="Qty Accepted"
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: 1, textAlign: "center" }}
                    />
                    <ListItemText
                      primary="Instructions"
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: 2 }}
                    />
                  </ListItem>
                  <Divider />

                  {asnData.sku_list?.map((item, index) => (
                    <Box key={index}>
                      <ListItem
                        sx={{
                          py: 1,
                          px: 2,
                          display: "flex",
                        }}
                      >
                        <ListItemText
                          primary={
                            orderData.line_items.find(
                              (x) => x.product_id === item.product,
                            )?.name || "-"
                          }
                          sx={{ flex: 3 }}
                        />
                        <ListItemText primary={item.sku} sx={{ flex: 2 }} />
                        <ListItemText
                          primary={item.quantity_requested || 0}
                          sx={{ flex: 1, textAlign: "center" }}
                        />
                        <ListItemText
                          primary={item.quantity_accepted || 0}
                          sx={{ flex: 1, textAlign: "center" }}
                        />
                        <ListItemText
                          primary={item.instructions || "-"}
                          sx={{ flex: 2 }}
                        />
                      </ListItem>
                      {item.attachment && (
                        <ListItem sx={{ py: 0, px: 2 }}>
                          <Typography variant="caption" color="primary">
                            Attachment: {item.attachment.fileName || "Document"}
                          </Typography>
                        </ListItem>
                      )}
                      <Divider />
                    </Box>
                  ))}

                  <ListItem
                    sx={{
                      py: 1,
                      px: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Subtotal: $
                      {parseFloat(orderData.subtotal_price || 0).toFixed(2)}
                    </Typography>
                  </ListItem>
                </List>
              </Paper>
            </Box>

            {/* ASN Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Purchase Order Information
              </Typography>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        ASN Number
                      </Typography>
                      <Typography variant="body2">{asnData.asn_no}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Supplier
                      </Typography>
                      <Typography variant="body2">
                        {typeof asnData.supplier === "object"
                          ? asnData.supplier?.name
                          : asnData.supplier || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Purchase Order
                      </Typography>
                      <Typography variant="body2">
                        {typeof asnData.purchase_order === "object"
                          ? asnData.purchase_order?.no
                          : asnData.purchase_order || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Warehouse
                      </Typography>
                      <Typography variant="body2">
                        {typeof asnData.warehouse === "object"
                          ? warehouseLabel(asnData.warehouse) || "-"
                          : asnData.warehouse || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Expected Dispatch Date
                      </Typography>
                      <Typography variant="body2">
                        {asnData.expected_dispatch_date
                          ? formatDate(asnData.expected_dispatch_date)
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Shipment Date
                      </Typography>
                      <Typography variant="body2">
                        {asnData.shipment_date
                          ? formatDate(asnData.shipment_date)
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        BOL Number
                      </Typography>
                      <Typography variant="body2">
                        {asnData.bol_no || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Status
                      </Typography>
                      <Chip
                        label={asnData.status?.toUpperCase() || "CREATED"}
                        color={
                          asnData.status === "Delivered"
                            ? "success"
                            : asnData.status === "Shipped"
                              ? "primary"
                              : "default"
                        }
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  {/* Additional Information */}
                  {asnData.additional_info && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Additional Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Driver Name
                          </Typography>
                          <Typography variant="body2">
                            {asnData.additional_info.driver_name || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Vehicle License
                          </Typography>
                          <Typography variant="body2">
                            {asnData.additional_info.vehicle_license || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: "1px solid var(--border-color)" }}>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default M2OOrderDetailsModal;
